import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props { children: ReactNode }
interface State { error: Error | null }

/**
 * The last line before a white screen.
 *
 * A class component because there is still no hook equivalent of
 * `componentDidCatch`. The copy follows the brand's rule -- plain, not
 * alarmist, and it says what to do next rather than apologising.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // No telemetry in this sprint (§Non-goals: no analytics), so the console is
    // genuinely where this goes. Left deliberate rather than forgotten.
    console.error('Unhandled error in the app shell', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="centred-page">
        <div className="card centred-page__card">
          <h1>Something went wrong on this screen.</h1>
          <p className="centred-page__body">
            Nothing was lost. Reloading usually clears it; if it happens again, the
            details below are what an engineer will ask for.
          </p>
          <pre className="centred-page__detail">{this.state.error.message}</pre>
          <button type="button" className="btn btn--primary" onClick={() => globalThis.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    )
  }
}
